import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RoleBasedQueue } from '@/components/queues/RoleBasedQueue';
import { ScorekeeperApplications } from '@/components/scorekeeping/ScorekeeperApplications';
import { VenueGameApproval } from '@/components/venue/VenueGameApproval';
import { useUserRole } from '@/hooks/useUserRole';
import { useGameManagement } from '@/hooks/useGameManagement';
import { GameManagementDashboard } from '@/components/games/GameManagementDashboard';
import { GameManagementIntegration } from '@/components/games/GameManagementIntegration';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import { useIsMobile } from '@/hooks/use-mobile';
import { 
  Calendar,
  Trophy,
  Users,
  CheckSquare,
  Building2
} from 'lucide-react';

const GameManagement: React.FC = () => {
  const { permissions, userId } = useUserRole();
  const { games, loading } = useGameManagement(userId);
  const isMobile = useIsMobile();

  const getAvailableTabs = () => {
    const tabs = [
      { value: 'queue', label: 'My Queue', icon: CheckSquare, show: true },
      { value: 'games', label: 'Games', icon: Calendar, show: true }
    ];

    if (permissions.role === 'scorekeeper' || permissions.isCoach || permissions.isTeamOwner) {
      tabs.push({ 
        value: 'scorekeeping', 
        label: permissions.role === 'scorekeeper' ? 'Scoring' : 'Scorekeepers', 
        icon: Trophy, 
        show: true 
      });
    }

    if (permissions.canManageVenues) {
      tabs.push({ 
        value: 'venue-approvals', 
        label: 'Venue Approvals', 
        icon: Building2, 
        show: true 
      });
    }

    return tabs.filter(tab => tab.show);
  };

  const tabs = getAvailableTabs();

  return (
    <GameManagementIntegration>
      <div className="flex flex-col min-h-screen bg-black w-full">
        {/* Desktop Header */}
        {!isMobile && <Header />}
        {/* Mobile Header */}
        {isMobile && <MobileHeader />}
        
        <main className={`flex-grow w-full ${
          isMobile 
            ? 'pt-16 mb-16 py-4 px-4' 
            : 'mt-24 mb-6 py-6 md:py-12 px-4 md:px-6'
        }`}>
          <div className="w-full max-w-6xl mx-auto">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-white">Game Management</h1>
              <p className="text-gray-400 mt-2">
                Role: {permissions.role}
                {permissions.isCoach && permissions.role !== 'coach' && ' | Coach'}
                {permissions.isTeamOwner && permissions.role !== 'team-owner' && ' | Team Owner'}
                {permissions.canManageVenues && ' | Venue Manager'}
              </p>
              <div className="mt-4 p-4 bg-gray-800 border border-gray-700 rounded-lg">
                <h3 className="text-lg font-semibold text-white mb-2">Tab Explanations:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-blue-400 font-medium">My Queue:</span>
                    <span className="text-gray-300 ml-2">Action items requiring your attention - confirmations, applications, urgent tasks</span>
                  </div>
                  <div>
                    <span className="text-green-400 font-medium">Games:</span>
                    <span className="text-gray-300 ml-2">All games dashboard - view, manage, and track all your games by status</span>
                  </div>
                </div>
              </div>
            </div>

          <Tabs defaultValue="queue" className="w-full">
            <TabsList className="flex w-full bg-gray-800 border border-gray-700 mb-6 rounded-lg p-1">
              {tabs.map((tab) => (
                <TabsTrigger 
                  key={tab.value}
                  value={tab.value} 
                  className="flex-1 text-white data-[state=active]:bg-blue-600 data-[state=active]:text-white flex items-center justify-center gap-2 px-4 py-2 rounded-md transition-all"
                >
                  <tab.icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="queue">
              <RoleBasedQueue />
            </TabsContent>

            <TabsContent value="games">
              <GameManagementDashboard />
            </TabsContent>

            {(permissions.role === 'scorekeeper' || permissions.isCoach || permissions.isTeamOwner) && (
              <TabsContent value="scorekeeping">
                <ScorekeeperApplications />
              </TabsContent>
            )}

            {permissions.canManageVenues && (
              <TabsContent value="venue-approvals">
                <VenueGameApproval venueId={userId || ''} />
              </TabsContent>
            )}
          </Tabs>
          </div>
        </main>
        
        {/* Desktop Footer */}
        {!isMobile && <Footer />}
        {/* Mobile Bottom Navigation */}
        {isMobile && <MobileBottomNav />}
      </div>
    </GameManagementIntegration>
  );
};

export default GameManagement;